import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { LogIn, Eye, EyeOff } from 'lucide-react';

import { wholesalerLogin } from '@/services/auth';
import { useAuthStore } from '@/store/auth.store';
import logoMark from '@/images/optoviklogo.png';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const mail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      toast.error('Некорректный email');
      return;
    }
    if (!password) {
      toast.error('Введите пароль');
      return;
    }
    try {
      setSubmitting(true);
      const res = await wholesalerLogin(mail, password);
      setSession({
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
        wholesaler: res.wholesaler,
      });
      toast.success('Добро пожаловать');
      const to = (location.state as { from?: string } | null)?.from ?? '/';
      navigate(to, { replace: true });
    } catch {
      // тост уже показан интерсептором
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-full flex items-center justify-center p-4">
      <div className="w-full max-w-md card p-5 sm:p-7">
        <div className="flex flex-col items-center text-center mb-6">
          <img
            src={logoMark}
            alt="Resstro Оптовик"
            className="h-14 w-14 rounded-2xl object-cover mb-3"
          />
          <div className="text-sm text-ink/50">Resstro Оптовик</div>
        </div>

        <form className="space-y-4" onSubmit={onSubmit}>
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              className="input"
              placeholder="you@company.ru"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              autoComplete="email"
              inputMode="email"
            />
          </div>

          <div>
            <label className="label">Пароль</label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                className="input pr-11"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md text-ink/40 hover:text-ink/70"
                tabIndex={-1}
                aria-label={showPass ? 'Скрыть пароль' : 'Показать пароль'}
              >
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button className="btn-primary w-full" type="submit" disabled={submitting}>
            <LogIn size={16} /> {submitting ? 'Входим…' : 'Войти'}
          </button>
        </form>

        <div className="mt-4 text-sm text-ink/60 text-center">
          Нет аккаунта?{' '}
          <Link to="/signup" className="inline-flex h-10 items-center text-orange-600 font-medium hover:underline">
            Регистрация
          </Link>
        </div>
      </div>
    </div>
  );
}
